import React, { RefObject, useEffect, useRef, useState } from 'react';
import { usePersonalizationContext } from '@contensis/personalization-react';
import { useSelector } from 'react-redux';
import mapJson from 'jsonpath-mapper';
import { User } from 'contensis-management-api/lib/models';

import ToolbarStyled from './Toolbar.styled';
import Divider from './Divider';
import { useToolbarNavigation } from './Toolbar.utils';
import { AppState } from '@/redux';
import { getPersonalizationEnabled } from '@/redux/selectors/app';
import { selectAuthState, selectUserDetails } from '@/redux/selectors/user';
import { CookieHelper } from '@/helpers/CookieHelper';

import { PanelTypes } from '@/types/Panel.types';
import Avatar, { AvatarProps } from '@/components/avatar/Avatar';
import { AvatarMappings } from '@/components/avatar/Avatar.mapper';
import EntryLink from '@/components/entryLink/EntryLink';
import Panel from '@/components/panel/Panel';
import DragButton from '@/components/dragButton/DragButton';
import { IconsDictionary } from '@/components/icon/Icon.types';
import Badge from '@/components/toolbarButton/Badge';
import ToolbarButton from '@/components/toolbarButton/ToolbarButton';
import useAppendToolbar from '@/hooks/useAppendToolbar';
import useEventListener from '@/hooks/useEvenListener';
import { useDragToolbar } from '@/hooks/useDragToolbar';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useShadowRoot from '@/hooks/useShadowRoot';
import { useWindowIsMobile } from '@/hooks/useWindowIsMobile';

const Toolbar = () => {
  const [panel, setPanel] = useState<PanelTypes | ''>('');
  const [prevPanel, setPrevPanel] = useState<PanelTypes | ''>('');
  const root = useShadowRoot();

  const togglePanel = (toggle: PanelTypes) => {
    if (toggle === panel) setPanel('');
    else setPanel(toggle);
  };

  useEffect(() => {
    if (panel !== prevPanel) {
      if (!panel && prevPanel) {
        // Set focus back to prevPanel's button
        const prevBtn = root?.querySelector(`#${prevPanel}`) as HTMLElement;
        if (prevBtn) prevBtn.focus();
      }
      setPrevPanel(panel);
    }
  }, [panel, prevPanel, root]);

  const user = useSelector(selectUserDetails) || ({} as User);

  const elToolbarRef: RefObject<HTMLDivElement> = useRef(null);

  useEventListener('keydown', evt => {
    if (evt.key === 'Escape') setPanel('');
  });

  const { loading, authenticated } = useSelector(selectAuthState);

  useEffect(() => {
    if (!loading && authenticated) setPanel('');
  }, [loading, authenticated]);

  const versionStatus = window?.ContensisEntryVersionStatus;
  const entryId = window?.ContensisEntryId;

  const isMobile = useWindowIsMobile();

  useToolbarNavigation('[data-id="toolbar-btn"]', !panel);
  useOnClickOutside('toolbar', () => setPanel(''));
  useDragToolbar(elToolbarRef);

  useEffect(() => {
    const path = window?.location?.href;
    if (decodeURIComponent(path).indexOf('&block-') !== -1) {
      CookieHelper.DeleteCookie('x-block-config');
      CookieHelper.DeleteCookie('x-blocks-changed');
    }
  }, []);

  const { initPosition } = useAppendToolbar(elToolbarRef);

  const personalizationFeature = useSelector<AppState, boolean>(
    getPersonalizationEnabled
  );
  const { audiences } = usePersonalizationContext();

  return (
    <ToolbarStyled
      id="toolbar"
      ref={elToolbarRef}
      role="toolbar"
      aria-label="Contensis Blocks Preview Toolbar"
    >
      <div
        data-toolbar={initPosition}
        className="toolbar"
        draggable="true"
        onDragStart={() => setPanel('')}
      >
        <DragButton action={() => setPanel('')} />
        <div className="toolbar__buttons">
          <Divider />
          {authenticated ? (
            <>
              <ToolbarButton
                id={PanelTypes.Blocks}
                aria-expanded={
                  panel === PanelTypes.Blocks || panel === PanelTypes.Version
                }
                aria-controls="blocks-panel"
                icon={IconsDictionary.Blocks}
                srText="Open panel"
                action={() => togglePanel(PanelTypes.Blocks)}
                isActive={
                  panel === PanelTypes.Blocks || panel === PanelTypes.Version
                }
                tooltipText="Blocks"
              />
              {personalizationFeature ? (
                <>
                  <Divider />
                  <ToolbarButton
                    id={PanelTypes.Personalization}
                    aria-expanded={panel === PanelTypes.Personalization}
                    aria-controls="personalization-panel"
                    icon={IconsDictionary.Personalization}
                    srText="Open personalization control panel"
                    action={() => togglePanel(PanelTypes.Personalization)}
                    isActive={panel === PanelTypes.Personalization}
                    tooltipText="Personalization"
                    badge={<Badge content={audiences.length} />}
                  />
                </>
              ) : null}
              <Divider />
              <ToolbarButton
                id={PanelTypes.VersionStatus}
                aria-expanded={panel === PanelTypes.VersionStatus}
                aria-controls="version-status-panel"
                icon={
                  versionStatus === 'latest'
                    ? IconsDictionary.ListEdit
                    : IconsDictionary.ListCheck
                }
                hasChevron
                srText="Open to change version status"
                action={() => togglePanel(PanelTypes.VersionStatus)}
                isActive={panel === PanelTypes.VersionStatus}
                tooltipText="Content version"
              />
              <Divider />
              <EntryLink
                text={`Open entry with the id of ${entryId}`}
                icon={IconsDictionary.Edit}
                tooltipText="Open entry"
              />
              <Divider />
              <ToolbarButton
                id={PanelTypes.Share}
                aria-expanded={panel === PanelTypes.Share}
                aria-controls="share-panel"
                icon={IconsDictionary.Share}
                srText="Open share panel"
                action={() => togglePanel(PanelTypes.Share)}
                isActive={panel === PanelTypes.Share}
                tooltipText="Share"
              />
              <Divider />
              {isMobile && (
                <>
                  <ToolbarButton
                    id={PanelTypes.Position}
                    aria-expanded={panel === PanelTypes.Position}
                    aria-controls={PanelTypes.Position}
                    icon={IconsDictionary.Cog}
                    srText="Open position panel"
                    action={() => togglePanel(PanelTypes.Position)}
                    isActive={panel === PanelTypes.Position}
                    tooltipText="Position"
                  />
                  <Divider />
                </>
              )}
              <ToolbarButton
                id={PanelTypes.Logout}
                aria-expanded={panel === PanelTypes.Logout}
                aria-controls={PanelTypes.Logout}
                srText="Open logout panel"
                action={() => togglePanel(PanelTypes.Logout)}
                isActive={panel === PanelTypes.Logout}
                tooltipText="Logout"
              >
                <Avatar {...mapJson<User, AvatarProps>(user, AvatarMappings)} />
              </ToolbarButton>
            </>
          ) : (
            <>
              <ToolbarButton
                id={PanelTypes.Login}
                aria-expanded={panel === PanelTypes.Login}
                aria-controls="login-panel"
                icon={IconsDictionary.User}
                srText="Open login screen"
                action={() => togglePanel(PanelTypes.Login)}
                isActive={panel === PanelTypes.Login}
                tooltipText="Login"
              />
            </>
          )}
        </div>
      </div>
      {panel && <Panel setPanel={setPanel} panel={panel} />}
    </ToolbarStyled>
  );
};

export default Toolbar;
